div<template>
  <form
    v-if="step == 'step-1'"
    class="uc-form-body"
    aria-label="Volunteer signup"
    @submit.prevent="nextPage()"
  >
    <div
      v-if="errors.length"
      class="step-errors"
      role="alert"
      aria-labelledby="errorsHeading"
    >
      <h5 id="errorsHeading">
        {{ $t('signUp.volunteerForm.step-1.errorsHeading') }}
      </h5>
      <ul>
        <li v-for="error in errors" v-bind:key="error">{{ error }}</li>
      </ul>
    </div>

    <div class="step-title">
      {{ $t('signUp.volunteerForm.step-1.title') }}
    </div>

    <div class="uc-column">
      <label for="inputEmail" class="uc-form-label">{{
        $t('signUp.volunteerForm.step-1.emailLabel')
      }}</label>
      <input
        id="inputEmail"
        type="email"
        class="uc-form-input"
        v-bind:class="{
          'uc-form-input--invalid': invalidInputs.indexOf('inputEmail') > -1,
        }"
        v-model="credentials.email"
        required
        autofocus
        autocomplete="email"
      />
      <p class="uc-form-subtext">
        {{ $t('signUp.volunteerForm.step-1.emailSubtext') }}
      </p>
    </div>

    <div class="uc-column">
      <label for="inputPassword" class="uc-form-label">
        {{ $t('signUp.volunteerForm.step-1.passwordLabel') }}
      </label>
      <input
        id="inputPassword"
        type="password"
        class="uc-form-input"
        v-bind:class="{
          'uc-form-input--invalid': invalidInputs.indexOf('inputPassword') > -1,
        }"
        v-model="credentials.password"
        required
        autocomplete="new-password"
      />
      <p class="uc-form-subtext">
        {{ $t('signUp.volunteerForm.step-1.passwordSubtext') }}
      </p>
    </div>

    <button class="uc-form-button" type="submit">
      {{ $t('general.continue') }}
    </button>

    <div v-if="msg !== ''" role="alert">{{ msg }}</div>
  </form>

  <form
    v-else-if="step == 'step-2'"
    class="uc-form-body"
    aria-label="Volunteer information"
    @submit.prevent="checkInputs($event)"
  >
    <div
      v-if="errors.length"
      class="step-errors"
      role="alert"
      aria-labelledby="volunteerInformationErrorsHeading"
    >
      <h5 id="volunteerInformationErrorsHeading">
        {{
          $t('signUp.volunteerForm.step-2.volunteerInformationErrorsHeading')
        }}
      </h5>
      <ul>
        <li v-for="error in errors" v-bind:key="error">{{ error }}</li>
      </ul>
    </div>

    <div class="step-title">
      {{ $t('signUp.volunteerForm.step-2.title') }}
    </div>

    <div class="uc-column">
      <!-- Fix for bug in Chrome where the first two fields are parsed as a username and password
       even if the HTML5 autocomplete attributes are set to the right values -->
      <label for="username" class="d-none">{{
        $t('signUp.volunteerForm.step-2.username')
      }}</label>
      <input
        type="text"
        class="d-none"
        id="username"
        v-model="credentials.email"
        autocomplete="username"
      />
      <label for="password" class="d-none">{{
        $t('signUp.volunteerForm.step-2.password')
      }}</label>
      <input
        type="password"
        class="d-none"
        id="password"
        v-model="credentials.password"
        autocomplete="new-password"
      />

      <label for="firstName" class="uc-form-label">{{
        $t('signUp.volunteerForm.step-2.firstName')
      }}</label>
      <input
        id="firstName"
        type="text"
        class="uc-form-input"
        v-bind:class="{
          'uc-form-input--invalid': invalidInputs.indexOf('firstName') > -1,
        }"
        v-model="profile.firstName"
        required
        autofocus
        autocomplete="given-name"
      />
    </div>

    <div class="uc-column">
      <label for="lastName" class="uc-form-label">{{
        $t('signUp.volunteerForm.step-2.lastName')
      }}</label>
      <input
        id="lastName"
        type="text"
        class="uc-form-input"
        v-bind:class="{
          'uc-form-input--invalid': invalidInputs.indexOf('lastName') > -1,
        }"
        v-model="profile.lastName"
        required
        autocomplete="family-name"
      />
    </div>

    <div class="uc-column">
      <label for="phoneNumber" class="uc-form-label">{{
        $t('signUp.volunteerForm.step-2.phoneNumber')
      }}</label>
      <vue-phone-number-input
        id="phoneNumber"
        class="phone-input"
        v-model="profile.phone"
        :required="true"
        :error="invalidInputs.indexOf('phone') > -1 && !phoneInputInfo.isValid"
        default-country-code="IR"
        no-country-selector
        clearable
        color="#555"
        valid-color="#16ba97"
        @update="onPhoneInputUpdate"
      />
      <p class="uc-form-subtext">
        {{ $t('signUp.volunteerForm.step-2.phoneNumberSubtext') }}
      </p>
    </div>

    <!-- <div class="uc-column">
        <label for="signup-source" class="uc-form-label">{{
          $t('signUp.volunteerForm.step-2.signUpSourceLabel')
        }}</label>
        <v-select
          id="signup-source"
          class="uc-form-select"
          v-model="signupSourceId"
          :options="signupSourcesOptions"
          :label="$t('signUp.volunteerForm.step-2.selectLabel')"
          :reduce="option => option.id"
          :searchable="false"
          :clearable="false"
          required
          :loading="isLoadingSignupSources"
        />
      </div> -->

    <div class="uc-column" v-if="shouldShowOtherSignupInput()">
      <input
        id="otherSignupSource"
        type="text"
        class="uc-form-input"
        v-model="otherSignupSource"
        v-bind:class="{
          'uc-form-input--invalid':
            invalidInputs.indexOf('otherSignupSource') > -1,
        }"
        autofocus
      />
      <p class="uc-form-subtext">
        {{ $t('signUp.volunteerForm.step-2.otherSignUpSourceSubtext') }}
      </p>
    </div>

    <div class="uc-form-checkbox">
      <input
        id="userAgreement"
        v-model="credentials.terms"
        type="checkbox"
        required
      />
      <label
        for="userAgreement"
        v-html="$t('signUp.volunteerForm.step-2.userAgreement')"
      />
    </div>

    <button class="uc-form-button" type="submit" :disabled="isRegistering">
      {{ $t('signUp.volunteerForm.step-2.signUpButton') }}
    </button>
    <loader class="register-loader" v-if="isRegistering" />
    <div v-if="msg" role="alert">{{ msg }}</div>
  </form>

  <form
    v-else-if="step == 'step-3'"
    class="uc-form-body"
    aria-label="confirm verification code"
    @submit.prevent="confirmVerificationCode()"
  >
    <div
      v-if="errors.length"
      class="step-errors"
      role="alert"
      aria-labelledby="errorsHeading"
    >
      <h5 id="errorsHeading">
        {{ $t('signUp.volunteerForm.step-1.errorsHeading') }}
      </h5>
      <ul>
        <li v-for="error in errors" v-bind:key="error">{{ error }}</li>
      </ul>
    </div>

    <div class="step-title">
      <p
        class="uc-form-header center"
        v-html="
          $t('verification.sendTo', { sendTo: phoneInputInfo.formatNational })
        "
      />
    </div>

    <div class="uc-form-element">
      <label for="verification-code">
        {{ $t('verification.label') }}
      </label>

      <SimpleOtpInput
        v-model="verificationCode"
        class="otp-with-effect"
        inputClasses="input-with-effect"
        :pasteDelayMs="192"
        withWebOtp
      >
        <template v-slot:extra>
          <span class="focus-border">
            <i></i>
          </span>
        </template>
      </SimpleOtpInput>
    </div>

    <div class="uc-form-subtext verification__sub-text text-center">
      {{ $t('verification.notReceiveText') }}
      <button
        :disabled="isSubmittingVerification || !countDownFlag"
        @click.prevent="sendCode"
        class="uc-link asText"
        :class="{ disableButton: !countDownFlag }"
        type="text"
      >
        {{ $t('verification.sendCodeButton') }}
        <span id="timer"></span>
      </button>
    </div>

    <button
      class="uc-form-button"
      type="submit"
      :disabled="
        isSubmittingVerification || cannotSubmitForm(v$.verificationCodeTrim)
      "
    >
      {{ $t('verification.confirmVerificationCodeButton') }}
    </button>

    <div v-if="msg !== ''" role="alert">{{ msg }}</div>
  </form>

  <div v-else class="uc-form-body">
    {{ $t('signUp.volunteerForm.step-1.errors.unexpectedError') }}
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import validator from 'validator'
import { useVuelidate } from '@vuelidate/core'
import LoggerService from '@/services/LoggerService'
import AuthService from '@/services/AuthService'
import VuePhoneNumberInput from 'vue-phone-number-input'
import Loader from '@/components/Loader.vue'
import NetworkService from '@/services/NetworkService'
import { backOff } from 'exponential-backoff'
import i18n from '@/i18n'
import { EVENTS, VERIFICATION_METHOD } from '@/consts'
import AnalyticsService from '@/services/AnalyticsService'
import SimpleOtpInput from 'vue-simple-otp-input'
import { helpers, maxLength, minLength, required } from '@vuelidate/validators'

export default {
  components: {
    VuePhoneNumberInput,
    Loader,
    SimpleOtpInput,
  },
  setup() {
    return { v$: useVuelidate() }
  },
  validations() {
    return {
      verificationCodeTrim: {
        required: helpers.withMessage(
          this.$root.$t('signUp.studentForm.validations.required'),
          required
        ),
        minLength: helpers.withMessage(
          'Must be 6 characters long',
          minLength(6)
        ),
        maxLength: helpers.withMessage(
          'Must be 6 characters long',
          maxLength(6)
        ),
      },
    }
  },
  data() {
    return {
      timerOn: true,
      countDownFlag: true,
      verificationCode: '',
      msg: '',
      errors: [],
      invalidInputs: [],
      credentials: {
        email: '',
        password: '',
        terms: false,
      },
      profile: {
        firstName: '',
        lastName: '',
        phone: '',
      },
      step: 'step-1',
      phoneInputInfo: {},
      isRegistering: false,
      signupSourcesOptions: [],
      signupSourceId: null,
      otherSignupSource: '',
      isLoadingSignupSources: false,
      isSubmittingVerification: false,
    }
  },
  computed: {
    ...mapState({
      user: state => state.user.user,
    }),
    ...mapGetters({
      isAutoFlowUser: 'user/isAutoFlowUser',
    }),
    verificationCodeTrim() {
      return this.verificationCode.trim()
    },
  },
  mounted() {
    // this.$router.push(`/${i18n.locale}/sign-up/volunteer/account`)
  },
  methods: {
    timer(remaining) {
      var m = Math.floor(remaining / 60)
      var s = remaining % 60

      m = m < 10 ? '0' + m : m
      s = s < 10 ? '0' + s : s
      document.getElementById('timer').innerHTML = m + ':' + s
      remaining -= 1

      if (remaining >= 0 && this.timerOn) {
        setTimeout(() => {
          this.countDownFlag = false
          this.timer(remaining)
        }, 1000)
        return
      }

      if (!this.timerOn) {
        // Do validate stuff here
        return
      }

      // Do timeout stuff here
      this.countDownFlag = true
    },
    async sendCode() {
      this.errors = []
      if (this.isSubmittingVerification) return
      this.isSubmittingVerification = true
      // this.loadingMessage = this.$root.$t('verification.loadingMessage')
      try {
        await AuthService.initiateVerificationV2({
          sendTo: this.phoneInputInfo.e164,
          verificationMethod: VERIFICATION_METHOD.SMS,
          userId: this.user.id,
          firstName: this.user.firstName,
        })

        this.timer(120)
        this.errors = []
      } catch (error) {
        const errorMsg =
          error?.response?.data?.err ??
          this.$root.$t('signUp.studentForm.responseError')
        this.errors.push(errorMsg)
      }

      this.isSubmittingVerification = false
    },
    isValidVerificationCode() {
      return !(
        this.verificationCode.length !== 6 ||
        isNaN(Number(this.verificationCode))
      )
    },
    cannotSubmitForm(el) {
      return !!el.$errors.length || !!el.$silentErrors.length
    },
    nextPage() {
      // validate input
      this.errors = []
      this.invalidInputs = []
      this.msg = ''
      if (!this.credentials.email) {
        this.errors.push(
          this.$root.$t('signUp.volunteerForm.step-1.errors.emailRequired')
        )
        this.invalidInputs.push('inputEmail')
      } else if (!validator.isEmail(this.credentials.email)) {
        // this is necessary because browsers ignore <input type="email"> until the
        // user actually tries to submit the form, which does not occur until step 2
        this.errors.push(
          this.$root.$t('signUp.volunteerForm.step-1.errors.notValidEmail', {
            credentialsEmail: this.credentials.email,
          })
        )
        this.invalidInputs.push('inputEmail')
      }
      if (!this.credentials.password) {
        this.errors.push(
          this.$root.$t('signUp.volunteerForm.step-1.errors.passwordRequired')
        )
        this.invalidInputs.push('inputPassword')
      }
      if (this.errors.length) {
        return
      }

      // check credentials
      AuthService.checkRegister({
        email: this.credentials.email,
        password: this.credentials.password,
      })
        .then(() => {
          this.step = 'step-2'
          // this.$router.push(`/${i18n.locale}/sign-up/volunteer/about`)
          this.getSignupSources()
        })
        .catch(err => {
          this.msg =
            err?.response?.data?.err ??
            this.$root.$t('signUp.volunteerForm.step-1.failedMessage')
          if (err?.response?.status !== 409 && err?.response?.status !== 422) {
            LoggerService.noticeError(err)
          }
        })
    },
    checkInputs() {
      this.errors = []
      this.invalidInputs = []

      // validate input
      if (!this.profile.firstName || !this.profile.lastName) {
        this.errors.push(
          this.$root.$t('signUp.volunteerForm.step-1.errors.nameRequired')
        )
      }
      if (!this.profile.firstName) {
        this.invalidInputs.push('firstName')
      }
      if (!this.profile.lastName) {
        this.invalidInputs.push('lastName')
      }
      if (!this.profile.phone) {
        this.errors.push(
          this.$root.$t('signUp.volunteerForm.step-1.errors.phoneRequired')
        )
        this.invalidInputs.push('phone')
      } else if (!this.phoneInputInfo.isValid || !this.phoneInputInfo.e164) {
        this.errors.push(
          this.$root.$t('signUp.volunteerForm.step-1.errors.notValidPhone', {
            profilePhone: this.profile.phone,
          })
        )
        this.invalidInputs.push('phone')
      }
      if (!this.credentials.terms) {
        this.errors.push(
          this.$root.$t('signUp.volunteerForm.step-1.errors.credentialsTerms')
        )
      }
      if (this.shouldShowOtherSignupInput() && !this.otherSignupSource) {
        this.errors.push(
          this.$root.$t('signUp.volunteerForm.step-1.errors.otherSignUpSource')
        )
        this.invalidInputs.push('otherSignupSource')
      }
      if (!this.errors.length) {
        this.submit()
      }
    },
    submit() {
      this.msg = ''
      if (this.isRegistering) return
      this.isRegistering = true
      AuthService.registerOpenVolunteerV2({
        email: this.credentials.email,
        password: this.credentials.password,
        terms: this.credentials.terms,
        firstName: this.profile.firstName,
        lastName: this.profile.lastName,
        phone: this.phoneInputInfo.e164,
        // signupSourceId: this.signupSourceId,
        // otherSignupSource: this.otherSignupSource,
      })
        .then(() => {
          this.isRegistering = false
          this.$store.dispatch('user/fetchUser')

          this.step = 'step-3'
          // this.$router.push(`/${i18n.locale}/sign-up/volunteer/verification`)
          this.sendCode()
          // this.$router.push(`/${i18n.locale}/verify`)
        })
        .catch(err => {
          this.isRegistering = false
          this.msg =
            err?.response?.data?.err ??
            this.$root.$t('signUp.volunteerForm.step-1.failedMessage')
          if (err?.response.status !== 409 && err?.response.status !== 422) {
            LoggerService.noticeError(err)
          }
        })
    },
    async confirmVerificationCode() {
      this.errors = []
      if (this.isSubmittingVerification) return
      this.isSubmittingVerification = true

      if (!this.isValidVerificationCode) {
        this.errors.push(
          this.$root.$t('verification.isValidVerificationCodeError')
        )

        this.isSubmittingVerification = false
        return
      }

      try {
        const {
          data: { success },
        } = await AuthService.confirmVerificationV2({
          verificationCode: this.verificationCode,
          sendTo: this.phoneInputInfo.e164,
          verificationMethod: VERIFICATION_METHOD.SMS,
        })

        if (success) {
          AnalyticsService.captureEvent(EVENTS.ACCOUNT_VERIFIED, {
            verificationMethod: VERIFICATION_METHOD.SMS,
          })
          const userUpdates = { verified: true }

          userUpdates.phoneVerified = true

          this.$store.dispatch('user/firstDashboardVisit', true)
          this.$store.dispatch('user/addToUser', userUpdates)

          if (this.isAutoFlowUser) {
            this.$router.push(`/${i18n.locale}/welcome`)
          } else {
            this.$router.push(`/${i18n.locale}/dashboard`)
          }
        } else {
          this.errors.push(
            this.$root.$t('verification.confirmVerificationError')
          )
        }
      } catch (error) {
        const errorMsg =
          error?.response?.data?.err ??
          this.$root.$t('signUp.studentForm.responseError')
        this.errors.push(errorMsg)
      }
      this.isSubmittingVerification = false
      this.errors = []
    },
    onPhoneInputUpdate(phoneInputInfo) {
      this.phoneInputInfo = phoneInputInfo
    },
    shouldShowOtherSignupInput() {
      if (this.isLoadingSignupSources || !this.signupSourcesOptions) {
        return false
      }
      const otherOption = this.signupSourcesOptions.find(
        s => s.name === 'Other'
      )
      return otherOption && otherOption.id === this.signupSourceId
    },
    async getSignupSources() {
      this.isLoadingSignupSources = true
      try {
        const response = await backOff(() =>
          NetworkService.getStudentSignupSources()
        )
        let allSources = response.data.signupSources

        // volunteer sources drop School/Teacher and replace Friend/Classmate with Friend
        allSources = allSources.filter(
          source => source.name !== 'School / Teacher'
        )
        allSources.find(source => source.name === 'Friend / Classmate').name =
          'Friend'
        this.signupSourcesOptions = allSources
      } catch (err) {
        LoggerService.noticeError(err)
      } finally {
        this.isLoadingSignupSources = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.uc-form-body {
  @include child-spacing(top, 25px);
  position: relative;
}

.phone-input {
  margin: 10px 0 2px;
}

.step-title {
  font-weight: bold;
  text-align: left;
}

.step-errors {
  color: #bf0000;
  font-size: 14px;
  text-align: left;
}

.d-none {
  display: none !important;
}

.register-loader {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  margin: 0;
}

.asText {
  background: none;
  border: none;
  margin: 0;
  padding: 0;
  cursor: pointer;
}

.disableButton {
  cursor: default;
  color: #a8aeb7;
}

html.is-rtl {
  .phone-input {
    direction: ltr;
  }

  .step-title {
    text-align: right;
  }

  .step-errors {
    text-align: right;
  }

  .register-loader {
    right: 50%;
    left: unset;
  }
}
</style>
